import React from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { resolveMediaUrl } from '../../../utilities/BL/PersonalQuickReaction';

/**
 * COMPONENTE: PersonalQuickReactionOverlay OVERLAY (DESIGN / CHILD VISUAL)
 * ------------------------------------------------------------------
 * Homologado 1:1 con la arquitectura probada de Encuesta B9 (2 QRs) y Rooster (1 QR).
 * Utiliza los estilos globales de `_OverlayQuizz.sass`:
 *   - .overlayquizz (con modificador .overlayquizz--rooster para 1 QR).
 *   - .overlayquizz__banner (con estados: --entering, --visible, --fadingOut, --idle).
 *   - .overlayquizz__banner--v2 (con imágenes responsivas --Md a 1536px y --Lg a 1920px).
 *   - .overlayquizz__banner--v2__QR-Overlay (--left y --right autocalibrados por CSS).
 *   - .overlayquizz-confetti (confetti GPU acelerado en Modo Ganador).
 *   - .overlayquizz__banner__winner (tarjeta de ganador con logos laterales).
 * ------------------------------------------------------------------
 * 
 * @param {Object} props - Propiedades del componente.
 * @param {string} [props.flowState='idle'] - Estado de animación ('idle' | 'entering' | 'visible' | 'fadingOut').
 * @param {number} [props.offsetY=0] - Desplazamiento vertical en píxeles.
 * @param {string} [props.mode='invitation'] - Modo del overlay ('invitation' | 'winner').
 * @param {string} [props.bannerMode='invitation'] - Alias de modo compatible.
 * @param {number} [props.qrCount=1] - Cantidad de códigos QR (1 o 2).
 * @param {string} [props.banner1536Url] - URL del cintillo personalizado de 1536px.
 * @param {string} [props.banner1920Url] - URL del cintillo personalizado de 1920px.
 * @param {string} [props.bannerImageMdUrl] - Alias de URL 1536px.
 * @param {string} [props.bannerImageLgUrl] - Alias de URL 1920px.
 * @param {string} [props.winnerName] - Nombre del aficionado ganador.
 * @param {number} [props.reactionTimeMs] - Mejor tiempo de reacción en ms.
 * @param {string} [props.badgeName] - Insignia de honor obtenida.
 * @param {boolean} [props.showConfetti=false] - Si se dispara la animación de confeti.
 * @returns {JSX.Element|null} Elemento de overlay o null cuando está inactivo.
 */
export function PersonalQuickReactionOverlay(props) {
    const {
        flowState = 'idle',
        offsetY = 0,
        mode = 'invitation',
        bannerMode,
        qrCount = 1,
        banner1536Url = '',
        banner1920Url = '',
        bannerImageMdUrl = '',
        bannerImageLgUrl = '',
        winnerName = '',
        reactionTimeMs = 0,
        badgeName = '',
        showConfetti = false
    } = props;

    // ZERO-CANVAS RULE: No renderizar nada en estado 'idle' (0% CPU/GPU)
    if (flowState === 'idle') {
        return null;
    }

    const effectiveMode = bannerMode || mode || 'invitation';
    const isWinnerMode = effectiveMode === 'winner';
    const isDualQr = Number(qrCount) === 2;

    const qrValue = `${typeof window !== 'undefined' ? window.location.origin : ''}/psl-qreaction?s=obs`;

    // Resolución de imágenes de cintillo con fallbacks predeterminados oficiales
    const rawMd = banner1536Url || bannerImageMdUrl;
    const rawLg = banner1920Url || bannerImageLgUrl;

    const mdBannerSource = rawMd
        ? resolveMediaUrl(rawMd)
        : '/img/overlays/rubaquiz/cintillo-ruba-1536-x-180-px.png';

    const lgBannerSource = rawLg
        ? resolveMediaUrl(rawLg)
        : '/img/overlays/rubaquiz/cintillo-ruba-1920-x-180-px.png';

    // 1 QR activa el modificador .overlayquizz--rooster; 2 QRs usa la clase base .overlayquizz
    const containerClasses = `overlayquizz ${isDualQr ? '' : 'overlayquizz--rooster'}`;

    return (
        <div className={containerClasses} style={{ bottom: `${offsetY}px` }}>
            {/* Confeti CSS acelerado por GPU en Modo Ganador */}
            {showConfetti && isWinnerMode && (
                <div className="overlayquizz-confetti">
                    {[...Array(40)].map((_, index) => (
                        <div key={index} className="overlayquizz-confetti__piece" />
                    ))}
                </div>
            )}

            <div className={`overlayquizz__banner overlayquizz__banner--${flowState}`}>
                {isWinnerMode ? (
                    /* ========================================================================= */
                    /* MODO GANADOR (WINNER MODE)                                                */
                    /* ========================================================================= */
                    <div className="overlayquizz__banner__winner" style={{ backgroundColor: '#23214a' }}>
                        {/* Logo Izquierdo */}
                        <div className="overlayquizz__logo overlayquizz__logo--left">
                            <img
                                src="/img/overlays/rubaquiz/interaktia-barrio9.png"
                                alt="Interaktia Logo Izquierdo"
                                style={{ height: '180px' }}
                            />
                        </div>

                        {/* Contenido / Anuncio del Récord */}
                        <div className="overlayquizz__content">
                            <p
                                className="overlayquizz__subtitle animate-pulse mb-1 text-uppercase"
                                style={{ color: '#ffd700', fontFamily: 'Montserrat, sans-serif' }}
                            >
                                ⚡ ¡RÉCORD DE REACCIÓN RÁPIDA EN VIVO! ⚡
                            </p>
                            <h2
                                className="overlayquizz__title text-uppercase"
                                style={{ color: '#ffffff', fontFamily: 'Montserrat, sans-serif', fontWeight: 900 }}
                            >
                                {winnerName || 'Aficionado Veloz'}
                            </h2>
                            {(reactionTimeMs > 0 || badgeName) && (
                                <p
                                    className="overlayquizz__credits text-uppercase"
                                    style={{ color: '#ffd700', fontFamily: 'Montserrat, sans-serif' }}
                                >
                                    {reactionTimeMs > 0 ? `⚡ ${reactionTimeMs} ms` : ''}
                                    {reactionTimeMs > 0 && badgeName ? '  |  ' : ''}
                                    {badgeName ? `🎯 ${badgeName}` : ''}
                                </p>
                            )}
                        </div>

                        {/* Logo Derecho */}
                        <div className="overlayquizz__logo overlayquizz__logo--right">
                            <img
                                src="/img/overlays/rubaquiz/interaktia-barrio9.png"
                                alt="Interaktia Logo Derecho"
                                style={{ height: '180px' }}
                            />
                        </div>
                    </div>
                ) : (
                    /* ========================================================================= */
                    /* MODO INVITACIÓN (INVITATION MODE: 1 QR / 2 QRs)                            */
                    /* ========================================================================= */
                    <div className="overlayquizz__banner--v2">
                        {/* Cintillo 1536px (visible a resoluciones <= 1536px) */}
                        <img
                            src={mdBannerSource}
                            className="overlayquizz__banner--v2__Image--Md"
                            alt="Cintillo Reacción Rápida 1536px"
                        />

                        {/* Cintillo 1920px (visible a resoluciones >= 1537px) */}
                        <img
                            src={lgBannerSource}
                            className="overlayquizz__banner--v2__Image--Lg"
                            alt="Cintillo Reacción Rápida 1920px"
                        />

                        {/* Código QR Izquierdo (1.888%/5.9895% en 1 QR Rooster, 9.1145%/15.625% en 2 QRs Encuesta B9) */}
                        <div className="overlayquizz__banner--v2__QR-Overlay overlayquizz__banner--v2__QR-Overlay--left">
                            <QRCodeSVG
                                value={qrValue}
                                size={256}
                                style={{ width: '92%', height: '92%', display: 'block' }}
                                bgColor="#d9d9d9"
                                fgColor="#000000"
                                level="H"
                                imageSettings={{
                                    src: '/img/overlays/rubaquiz/interaktia-transparent.png',
                                    x: undefined,
                                    y: undefined,
                                    height: 52,
                                    width: 52,
                                    excavate: true
                                }}
                            />
                        </div>

                        {/* Código QR Derecho (79.3620%/75.3125% - únicamente renderizado en modo 2 QRs) */}
                        {isDualQr && (
                            <div className="overlayquizz__banner--v2__QR-Overlay overlayquizz__banner--v2__QR-Overlay--right">
                                <QRCodeSVG
                                    value={qrValue}
                                    size={256}
                                    style={{ width: '92%', height: '92%', display: 'block' }}
                                    bgColor="#d9d9d9"
                                    fgColor="#000000"
                                    level="H"
                                    imageSettings={{
                                        src: '/img/overlays/rubaquiz/interaktia-transparent.png',
                                        x: undefined,
                                        y: undefined,
                                        height: 52,
                                        width: 52,
                                        excavate: true
                                    }}
                                />
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

export default PersonalQuickReactionOverlay;
